import { useContext, useEffect } from 'react';

import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import useWarningConfirmation from 'components/ConfirmationModals/hooks/useWarningConfirmation';

import SelectedEntityContext from '../contexts/SelectedEntityContext';

/**
 * While the slideout is in override mode, selecting a different driver in the
 * grid would silently swap the driver out from under the in-progress override
 * and corrupt its state. This registers a guard on SelectedEntityContext so
 * such a switch first prompts the unsaved-changes confirmation; on confirm it
 * leaves override mode (clearing staged overrides + preview) and then proceeds
 * to the new driver.
 *
 * Rendered only while in override mode, so the guard is registered exactly for
 * that window and torn down on exit.
 */
const OverrideEntityChangeGuard = () => {
  const { exitOverrideMode, bonusOverview } = useContext(BonusOverviewContext);
  const { setEntityChangeGuard } = useContext(SelectedEntityContext);
  const { getConfirmationThat, DangerConfirmation } = useWarningConfirmation({
    title: 'Unsaved changes',
    continueText: 'Discard changes',
  });

  const driver = bonusOverview?.driver;

  useEffect(() => {
    setEntityChangeGuard((proceed) => {
      getConfirmationThat(
        `You have an in-progress override${
          driver ? ` for ${driver}` : ''
        }. Switching to another driver will discard it. Do you want to continue?`,
      ).then((isConfirmed) => {
        if (!isConfirmed) {
          return;
        }
        exitOverrideMode();
        proceed();
      });
    });

    return () => {
      setEntityChangeGuard(undefined);
    };
  }, [setEntityChangeGuard, getConfirmationThat, exitOverrideMode, driver]);

  return DangerConfirmation;
};

export default OverrideEntityChangeGuard;
